import { createContext, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import { Amplify } from 'aws-amplify';
import {
  signIn,
  signOut,
  signUp,
  confirmSignUp,
  resendSignUpCode,
  resetPassword,
  confirmResetPassword,
  confirmSignIn,
  getCurrentUser,
  fetchAuthSession
} from 'aws-amplify/auth';
import { amplifyConfig } from '../config';

Amplify.configure(amplifyConfig);

const initialState = {
  isAuthenticated: true,
  isNewPasswordRequired: false,
  isInitialized: false,
  user: null,
  email: null
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      isNewPasswordRequired: false,
      user
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      isNewPasswordRequired: false,
      user
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    isNewPasswordRequired: false,
    user: null
  }),
  NEW_PASSWORD_REQUIRED: (state, action) => {
    const { user, email } = action.payload;
    return {
      ...state,
      isAuthenticated: false,
      isNewPasswordRequired: true,
      user,
      email
    };
  },
  REGISTER: (state) => ({ ...state }),
  VERIFY_CODE: (state) => ({ ...state }),
  RESEND_CODE: (state) => ({ ...state }),
  PASSWORD_RECOVERY: (state) => ({ ...state }),
  PASSWORD_RESET: (state) => ({ ...state })
};

const reducer = (state, action) => (handlers[action.type]
  ? handlers[action.type](state, action)
  : state);

export const AuthContext = createContext({
  ...initialState,
  method: 'Amplify',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  verifyCode: () => Promise.resolve(),
  resendCode: () => Promise.resolve(),
  passwordRecovery: () => Promise.resolve(),
  passwordReset: () => Promise.resolve(),
  completeNewPassword: () => Promise.resolve()
});

export const AuthProvider = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        const user = await getCurrentUser();
        const session = await fetchAuthSession();

        // Here you should extract the complete user profile to make it
        // available in your entire app.
        // The auth state only provides basic information.

        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: true,
            user: {
              id: user.userId,
              email: session.tokens?.idToken?.payload?.email
            }
          }
        });
      } catch (error) {
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null
          }
        });
      }
    };

    initialize();
  }, []);

  const login = async (email, password) => {
    const { nextStep } = await signIn({ username: email, password });

    if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      dispatch({
        type: 'NEW_PASSWORD_REQUIRED',
        payload: { user: null, email }
      });
    } else if (nextStep.signInStep !== 'DONE') {
      console.error(`Unable to login, because challenge "${nextStep.signInStep}" is mandated and we did not handle this case.`);
    } else {
      const session = await fetchAuthSession();
      dispatch({
        type: 'LOGIN',
        payload: {
          user: {
            id: session.tokens?.idToken?.payload?.sub,
            email: session.tokens?.idToken?.payload?.email
          }
        }
      });
    }
  };

  const logout = async () => {
    await signOut();
    dispatch({
      type: 'LOGOUT'
    });
  };

  const register = async (email, password) => {
    await signUp({
      username: email,
      password,
      options: {
        userAttributes: { email }
      }
    });
    dispatch({
      type: 'REGISTER'
    });
  };

  const verifyCode = async (username, code) => {
    await confirmSignUp({ username, confirmationCode: code });
    dispatch({
      type: 'VERIFY_CODE'
    });
  };

  const resendCode = async (username) => {
    await resendSignUpCode({ username });
    dispatch({
      type: 'RESEND_CODE'
    });
  };

  const passwordRecovery = async (username) => {
    await resetPassword({ username });
    dispatch({
      type: 'PASSWORD_RECOVERY'
    });
  };

  const passwordReset = async (username, code, newPassword) => {
    await confirmResetPassword({ username, confirmationCode: code, newPassword });
    dispatch({
      type: 'PASSWORD_RESET'
    });
  };

  const completeNewPassword = async (user, email, newPassword) => {
    await confirmSignIn({ challengeResponse: newPassword });
    // After confirming the new password, the user is signed in
    const session = await fetchAuthSession();
    dispatch({
      type: 'LOGIN',
      payload: {
        user: {
          id: session.tokens?.idToken?.payload?.sub,
          email: session.tokens?.idToken?.payload?.email
        }
      }
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'Amplify',
        login,
        logout,
        register,
        verifyCode,
        resendCode,
        passwordRecovery,
        passwordReset,
        completeNewPassword
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export const AuthConsumer = AuthContext.Consumer;
